/**
 * ChatExpandedDrawer - Full-height drawer for expanded chat view
 *
 * Shows chat list on left, messages on right
 * Styled with Notion-like design
 */

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Search, Users, Send, Loader2, Reply, Edit2, Trash2, Check, CheckCheck, XCircle, CornerDownRight, MoreHorizontal, LogOut, Archive, RotateCcw, ChevronDown, ChevronRight, UserPlus, EyeOff, Pencil, Paperclip, Mic, Smile, Sparkles, Copy, Forward, Pin, BellOff, Bell, SearchIcon, FileText, ImageIcon, Film, Music2, Video, Bookmark, Clock, AlertCircle, RefreshCw, Globe, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTeamChatStore, MessageAttachment, TeamChatMessage } from '../../lib/stores/teamChatStore';
import { supabase } from '../../lib/supabase';
import { PresenceDot } from '../ui/PresenceDot';
import { useAuth } from '../../lib/auth';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { useChatMessaging } from './hooks/useChatMessaging';
import { AddMembersModal } from './AddMembersModal';
import { AudioRecorder } from './AudioRecorder';
import { MessageContent } from './MessageContent';
import { uploadChatFile } from '../../lib/services/teamChatUploadService';
import { ImageLightbox } from './ImageLightbox';
import { MessageSearchBar } from './MessageSearchBar';
import { ForwardMessageModal } from './ForwardMessageModal';
import { PinnedMessagesPanel } from './PinnedMessagesPanel';
import { SavedMessagesPanel } from './SavedMessagesPanel';
import { CustomStatusPopover } from './CustomStatusPopover';
import { GifPicker } from './GifPicker';
import { ScheduleMessagePopover } from './ScheduleMessagePopover';
import { ThreadPanel } from './ThreadPanel';
import { usePresenceStore } from '../../lib/stores/presenceStore';
import { useCalendarStore } from '../../lib/stores/calendarStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';

type MeetProvider = 'google_meet' | 'microsoft_teams';

// ─────────────────────────────────────────────────────────────────────────────
// SimpleEmojiPicker: grid locale senza fetch CDN
// ─────────────────────────────────────────────────────────────────────────────

const EMOJI_GRID = [
  '😀','😁','😂','🤣','😃','😄','😅','😆','😇','😊',
  '😋','😎','😍','🥰','😘','😗','😙','😚','🙂','🤗',
  '🤔','😐','😑','😶','🙄','😏','😣','😥','😮','🤐',
  '😯','😪','😫','😴','😌','😛','😜','😝','🤤','😒',
  '😓','😔','😕','🙃','🤑','😲','😤','😠','😡','🤬',
  '😈','💀','☠️','💩','🤡','👹','👺','👻','👽','🤖',
  '👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞',
  '🤟','🤘','👍','👎','✊','👊','🤛','🤜','👏','🙌',
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','❣️',
  '💕','💞','💓','💗','💖','💘','💝','💟','☮️','✨',
  '🎉','🎊','🎈','🎁','🎀','🏆','🥇','⭐','🌟','💫',
  '🔥','💥','❄️','🌈','☀️','🌙','⚡','🌊','🍀','🌸',
];

function SimpleEmojiPicker({ onSelect }: { onSelect: (emoji: string) => void }) {
  return (
    <div
      className="bg-white rounded-2xl shadow-2xl p-2 overflow-y-auto"
      style={{
        width: 280,
        maxHeight: 260,
        border: '1px solid rgba(55,53,47,0.12)',
      }}
    >
      <div className="grid" style={{ gridTemplateColumns: 'repeat(10, 1fr)', gap: 1 }}>
        {EMOJI_GRID.map((e) => (
          <button
            key={e}
            onClick={() => onSelect(e)}
            className="flex items-center justify-center rounded-lg text-lg transition-colors"
            style={{ width: 26, height: 26, fontSize: 17 }}
            onMouseEnter={(el) => (el.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.07)')}
            onMouseLeave={(el) => (el.currentTarget.style.backgroundColor = 'transparent')}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

interface ChatExpandedDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onNewChat: () => void;
}

export function ChatExpandedDrawer({ isOpen, onClose, onNewChat }: ChatExpandedDrawerProps) {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const {
    chats,
    archivedChats,
    loading,
    selectedChat,
    selectedChatId,
    selectChat,
    leaveChat,
    archiveChat,
    fetchArchivedChats,
    restoreChat,
    markChatAsUnread,
    renameChat,
    typingUsers,
    organizationUsers,
    addReaction,
    removeReaction,
    togglePinMessage,
    toggleMuteChat,
    forwardMessage,
    savedMessageIds,
    saveMessage,
    unsaveMessage,
    fetchSavedMessageIds,
    removeParticipant,
  } = useTeamChatStore();

  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showParticipantsList, setShowParticipantsList] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  // Rich media state
  const [isRecording, setIsRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Staged files: file pronti per essere inviati (drag-and-drop / selezione)
  type StagedFile = {
    id: string;
    file: File;
    preview: string | null; // object URL per immagini
    attachmentType: MessageAttachment['type'];
  };
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);

  // Upload in-progress: file mostrati in chat mentre si caricano
  type UploadingItem = {
    id: string;
    name: string;
    size: number;
    attachmentType: MessageAttachment['type'];
    preview: string | null; // object URL per immagini
    progress: number; // 0-100
  };
  const [uploadingItems, setUploadingItems] = useState<UploadingItem[]>([]);

  // AI Summary — threshold + cache condivisa
  // Rigenera solo se: 10+ messaggi nuovi E summary più vecchio di 30min
  type ChatSummary = { text: string; nextSteps?: string[]; generatedAt: string; lastMessageId: string | null };
  const [summaryData, setSummaryData] = useState<ChatSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const generatingChatsRef = useRef<Set<string>>(new Set());
  const SUMMARY_MIN_NEW_MESSAGES = 10;
  const SUMMARY_MIN_AGE_MS = 30 * 60 * 1000; // 30 minuti

  // Panels state
  const [showPinnedPanel, setShowPinnedPanel] = useState(false);
  const [showSavedPanel, setShowSavedPanel] = useState(false);
  const [showStatusPopover, setShowStatusPopover] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showSchedulePopover, setShowSchedulePopover] = useState(false);
  const [creatingMeet, setCreatingMeet] = useState(false);
  // Meeting provider availability (Google Meet / Microsoft Teams)
  const { settings } = useSettingsStore();
  const [meetGoogleAvailable, setMeetGoogleAvailable] = useState(false);
  const [meetMicrosoftConnected, setMeetMicrosoftConnected] = useState(false);
  const [showMeetMenu, setShowMeetMenu] = useState(false);
  const teamsMeetAvailable = meetMicrosoftConnected && !!(settings as any)?.microsoft_sync_teams;
  const [threadRootMessage, setThreadRootMessage] = useState<TeamChatMessage | null>(null);
  const { getCustomStatus } = usePresenceStore();
  const myStatus = userProfile?.id ? getCustomStatus(userProfile.id) : null;

  // @mention state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);

  // Lightbox immagini
  const [lightboxImages, setLightboxImages] = useState<{ url: string; name?: string }[] | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Message search dentro la chat
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  // Forward modal
  const [forwardingMessage, setForwardingMessage] = useState<TeamChatMessage | null>(null);

  // Emoji picker (per input)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerAnchorRef = useRef<HTMLButtonElement>(null);

  // Reaction picker
  const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👀'];
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState<string | null>(null);
  const [reactionPickerPos, setReactionPickerPos] = useState<{ x: number; y: number } | null>(null);

  const closeMenu = () => { setMenuOpenId(null); setMenuPos(null); };

  // ── AI Summary helpers ───────────────────────────────────────────────────────

  const chat = selectedChat();

  const {
    messages,
    loadingMessages,
    inputValue,
    setInputValue,
    editingId,
    editValue,
    setEditValue,
    replyingTo,
    messagesEndRef,
    messagesContainerRef,
    inputRef,
    handleScroll,
    handleSend,
    handleKeyDown,
    handleInputChange,
    startEditing,
    cancelEditing,
    handleEdit,
    handleEditKeyDown,
    handleDelete,
    handleReply,
    cancelReply,
    loadingOlderMessages,
    retryMessage,
    discardFailedMessage,
  } = useChatMessaging();

  // Genera summary per un chatId specifico — salva SEMPRE su DB, aggiorna UI solo se ancora attivo
  const generateSummaryForChat = useCallback(async (
    chatId: string,
    chatMessages: typeof messages,
    existingSummary: ChatSummary | null,
  ) => {
    if (generatingChatsRef.current.has(chatId)) return;
    generatingChatsRef.current.add(chatId);

    // Mostra loading solo se è la chat attualmente visualizzata
    const isCurrentChat = () => useTeamChatStore.getState().selectedChatId === chatId;
    if (isCurrentChat()) setSummaryLoading(true);

    try {
      let msgsToSummarize = chatMessages;
      if (existingSummary?.lastMessageId) {
        const lastIdx = chatMessages.findIndex(m => m.id === existingSummary.lastMessageId);
        if (lastIdx !== -1 && lastIdx < chatMessages.length - 1) {
          msgsToSummarize = chatMessages.slice(lastIdx + 1);
        }
      }
      msgsToSummarize = msgsToSummarize.slice(-50);

      const chatText = msgsToSummarize
        .map(m => `${m.sender?.full_name || 'Utente'}: ${m.content || '[allegato]'}`)
        .join('\n');

      const prevContext = existingSummary
        ? `\n\nRIASSUNTO PRECEDENTE (${new Date(existingSummary.generatedAt).toLocaleString('it-IT')}):\n${existingSummary.text}`
        : '';

      const systemPrompt = `Rispondi SOLO con JSON valido, nient'altro. Analizza la conversazione e restituisci:
{"summary":"riassunto in 1-2 frasi brevi di cosa si è discusso/deciso","nextSteps":["azione concreta 1","azione concreta 2"]}
Regole:
- summary: max 2 frasi, dritto al punto, in italiano
- nextSteps: array di azioni concrete da fare (chi deve fare cosa). Se non ci sono next steps, array vuoto []
- Se è solo chit-chat: summary "Scambio di saluti." e nextSteps []`;
      const userPrompt = `${prevContext}\n\nMESSAGGI:\n${chatText}`;

      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt },
          ],
          model: 'haiku',
          temperature: 0.3,
          max_tokens: 800,
        }),
      });

      if (!res.ok) throw new Error('Errore API');

      const json = await res.json();
      const raw: string = json.content || '';

      // Pulisci eventuali code fences markdown e testo prima/dopo il JSON
      let cleanJson = raw;
      // Rimuovi tutto prima e dopo i code fences, se presenti
      const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
      if (fenceMatch) {
        cleanJson = fenceMatch[1];
      }
      // Prova a estrarre il JSON anche senza code fences
      if (!fenceMatch) {
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        if (jsonMatch) cleanJson = jsonMatch[0];
      }
      cleanJson = cleanJson.trim();

      let parsedText = raw;
      let nextSteps: string[] = [];
      try {
        const parsed = JSON.parse(cleanJson);
        parsedText = parsed.summary || raw;
        nextSteps = Array.isArray(parsed.nextSteps) ? parsed.nextSteps : [];
      } catch {
        // fallback: usa il testo raw come summary
      }

      const lastMsg = msgsToSummarize[msgsToSummarize.length - 1];
      const newSummary: ChatSummary = {
        text: parsedText,
        nextSteps: nextSteps.length > 0 ? nextSteps : undefined,
        generatedAt: new Date().toISOString(),
        lastMessageId: lastMsg?.id ?? null,
      };

      // Salva SEMPRE su DB, indipendentemente dalla chat attiva
      await supabase.from('team_chats').update({ ai_summary: newSummary }).eq('id', chatId);

      // Aggiorna UI solo se è ancora la chat visualizzata
      if (isCurrentChat()) setSummaryData(newSummary);
    } catch {
      // Errore silenzioso — è background
    } finally {
      generatingChatsRef.current.delete(chatId);
      if (isCurrentChat()) setSummaryLoading(false);
    }
  }, []);

  // Carica IDs messaggi salvati
  useEffect(() => { if (isOpen) fetchSavedMessageIds(); }, [isOpen, fetchSavedMessageIds]);

  // Carica summary dal DB. Auto-rigenera SOLO se soglia messaggi + tempo raggiunta.
  const prevSelectedChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (selectedChatId === prevSelectedChatIdRef.current) return;
    prevSelectedChatIdRef.current = selectedChatId ?? null;

    setSummaryData(null);
    setSummaryLoading(false);
    // Pulisci staged files quando si cambia chat
    setStagedFiles(prev => {
      prev.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
      return [];
    });
    if (!selectedChatId) return;

    if (generatingChatsRef.current.has(selectedChatId)) {
      setSummaryLoading(true);
      return;
    }

    const chatId = selectedChatId;
    supabase.from('team_chats').select('ai_summary').eq('id', chatId).single()
      .then(({ data }) => {
        if (useTeamChatStore.getState().selectedChatId !== chatId) return;
        const existing = data?.ai_summary as ChatSummary | null;
        if (existing) setSummaryData(existing);

        // Verifica soglie prima di rigenerare
        setTimeout(() => {
          const state = useTeamChatStore.getState();
          const currentMessages = state.selectedChatMessages();
          if (!currentMessages.length) return;

          const lastMsg = currentMessages[currentMessages.length - 1];
          if (existing?.lastMessageId === lastMsg?.id) return; // già aggiornato

          // Conta messaggi nuovi dal last summary
          let newMsgCount = currentMessages.length;
          if (existing?.lastMessageId) {
            const lastIdx = currentMessages.findIndex(m => m.id === existing.lastMessageId);
            if (lastIdx !== -1) newMsgCount = currentMessages.length - lastIdx - 1;
          }

          // Soglia messaggi: servono almeno N messaggi nuovi
          if (newMsgCount < SUMMARY_MIN_NEW_MESSAGES) return;

          // Soglia tempo: non rigenerare se il summary è troppo recente
          if (existing?.generatedAt) {
            const age = Date.now() - new Date(existing.generatedAt).getTime();
            if (age < SUMMARY_MIN_AGE_MS) return;
          }

          generateSummaryForChat(chatId, currentMessages, existing);
        }, 2000);
      });
  }, [selectedChatId, generateSummaryForChat]);

  // Rigenera summary manualmente (bottone "Rigenera")
  const handleRegenerateSummary = useCallback(() => {
    if (!selectedChatId) return;
    const currentMessages = useTeamChatStore.getState().selectedChatMessages();
    if (!currentMessages.length) return;
    generateSummaryForChat(selectedChatId, currentMessages, summaryData);
  }, [selectedChatId, summaryData, generateSummaryForChat]);

  const getChatDisplayName = (c: typeof chats[0]) => {
    if (c.chat_type === 'visitor') {
      return c.visitor?.visitor_name || c.visitor?.visitor_email || 'Visitor';
    }
    if (c.is_group && c.name) return c.name;
    const other = c.participants?.find(p => p.user_id !== userProfile?.id);
    return other?.user?.full_name || 'Chat';
  };

  const getChatAvatar = (c: typeof chats[0]) => {
    if (c.chat_type === 'visitor') return null;
    if (c.is_group) return null;
    const other = c.participants?.find(p => p.user_id !== userProfile?.id);
    return other?.user?.profile_image_url;
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatChatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Ieri';
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  };

  const filteredChats = useMemo(() => {
    if (!searchQuery.trim()) return chats;
    const q = searchQuery.toLowerCase();
    return chats.filter(c => getChatDisplayName(c).toLowerCase().includes(q));
  }, [chats, searchQuery]);

  const currentTypingUsers = selectedChatId ? (typingUsers[selectedChatId] || []) : [];

  // ── @mention helpers ────────────────────────────────────────────────────────

  const mentionResults = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    const participants = (chat?.participants || [])
      .map(p => p.user).filter(Boolean) as { id: string; full_name: string; profile_image_url?: string | null }[];
    const all = [
      ...participants,
      ...organizationUsers.filter(u => !participants.some(p => p.id === u.id))
    ];
    return all.filter(u => u.full_name.toLowerCase().includes(q)).slice(0, 6);
  }, [mentionQuery, chat, organizationUsers]);

  const handleMentionSelect = useCallback((user: { id: string; full_name: string }) => {
    const before = inputValue.slice(0, mentionStart);
    const after = inputValue.slice(mentionStart + 1 + (mentionQuery?.length || 0));
    const newValue = `${before}@[${user.full_name}] ${after}`;
    setInputValue(newValue);
    setMentionQuery(null);
    setMentionIndex(0);
    inputRef.current?.focus();
    setTimeout(() => {
      const pos = mentionStart + user.full_name.length + 4; // @[ ] + space
      inputRef.current?.setSelectionRange(pos, pos);
    }, 0);
  }, [inputValue, mentionStart, mentionQuery, setInputValue, inputRef]);

  const detectMention = useCallback((value: string, cursorPos: number) => {
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase());
      setMentionStart(textBeforeCursor.lastIndexOf('@'));
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  }, []);

  // ── Emoji helpers ───────────────────────────────────────────────────────────

  const insertEmoji = useCallback((emoji: string) => {
    const input = inputRef.current;
    const cursor = input?.selectionStart ?? inputValue.length;
    const before = inputValue.slice(0, cursor);
    const after = inputValue.slice(cursor);
    const newValue = `${before}${emoji}${after}`;
    setInputValue(newValue);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
    setTimeout(() => {
      const pos = cursor + emoji.length;
      inputRef.current?.setSelectionRange(pos, pos);
    }, 0);
  }, [inputValue, setInputValue, inputRef]);

  // ── Lightbox helpers ─────────────────────────────────────────────────────────

  const openLightbox = useCallback((imageUrl: string, imageName?: string) => {
    // Raccogli tutte le immagini della chat corrente
    const allImages: { url: string; name?: string }[] = [];
    messages.forEach(msg => {
      if (msg.attachments) {
        msg.attachments.forEach(att => {
          if (att.type === 'image' && att.url) {
            allImages.push({ url: att.url, name: att.name });
          }
        });
      }
    });
    const idx = allImages.findIndex(img => img.url === imageUrl);
    setLightboxImages(allImages.length > 0 ? allImages : [{ url: imageUrl, name: imageName }]);
    setLightboxIndex(idx >= 0 ? idx : 0);
  }, [messages]);

  // ── Copy message helper ─────────────────────────────────────────────────────

  const handleCopyMessage = useCallback((msg: TeamChatMessage) => {
    const text = msg.content || '';
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Messaggio copiato');
    }).catch(() => {
      toast.error('Errore nella copia');
    });
  }, []);

  // ── Navigate to message (for search) ──────────────────────────────────────

  const handleNavigateToMessage = useCallback(async (messageId: string) => {
    setHighlightedMessageId(messageId);

    // Prima prova a trovare il messaggio nel DOM
    let el = document.getElementById(`msg-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setHighlightedMessageId(null), 2000);
      return;
    }

    // Messaggio non nel DOM — devo caricare messaggi più vecchi fino a trovarlo
    if (!selectedChatId) return;
    const chatMessages = messages[selectedChatId] || [];
    const oldestLoaded = chatMessages[0]?.created_at;

    // Fetch il messaggio dal DB per conoscere il suo created_at
    const { data: targetMsg } = await supabase
      .from('team_chat_messages')
      .select('created_at')
      .eq('id', messageId)
      .single();

    if (!targetMsg) return;

    // Se il messaggio è più vecchio di quelli caricati, carico il batch che lo include
    if (oldestLoaded && new Date(targetMsg.created_at) < new Date(oldestLoaded)) {
      // Carica tutti i messaggi dal target fino ai messaggi attuali
      const { data: olderData } = await supabase
        .from('team_chat_messages')
        .select(`
          *,
          sender:user_profiles!sender_id(id, full_name, profile_image_url, avatar_url),
          reply_to:team_chat_messages!reply_to_id(id, content, sender:user_profiles!sender_id(id, full_name)),
          reactions:team_chat_message_reactions(emoji, user_id)
        `)
        .eq('chat_id', selectedChatId)
        .eq('is_deleted', false)
        .gte('created_at', targetMsg.created_at)
        .lt('created_at', oldestLoaded)
        .order('created_at', { ascending: true });

      if (olderData && olderData.length > 0) {
        // Normalizza e prepend
        const normalized = olderData.map((m: any) => {
          if (m.sender) m.sender.profile_image_url = m.sender.avatar_url || m.sender.profile_image_url || null;
          const rawReactions: { emoji: string; user_id: string }[] = m.reactions || [];
          const reactionMap = new Map<string, string[]>();
          for (const r of rawReactions) { const u = reactionMap.get(r.emoji) || []; u.push(r.user_id); reactionMap.set(r.emoji, u); }
          m.reactions = Array.from(reactionMap.entries()).map(([emoji, user_ids]) => ({ emoji, user_ids, count: user_ids.length }));
          return m;
        });

        useTeamChatStore.setState(state => ({
          messages: {
            ...state.messages,
            [selectedChatId!]: [...normalized, ...(state.messages[selectedChatId!] || [])],
          },
          hasMoreMessages: { ...state.hasMoreMessages, [selectedChatId!]: true },
        }));
      }
    }

    // Attendi il render e poi scrolla
    await new Promise(r => setTimeout(r, 100));
    el = document.getElementById(`msg-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => setHighlightedMessageId(null), 2000);
  }, [selectedChatId, messages]);

  // ── Pin helper ──────────────────────────────────────────────────────────────

  const handleTogglePin = useCallback(async (msg: TeamChatMessage) => {
    try {
      await togglePinMessage(msg.id, msg.chat_id);
      toast.success((msg as any).is_pinned ? 'Messaggio rimosso dai fissati' : 'Messaggio fissato');
    } catch {
      toast.error('Errore');
    }
  }, [togglePinMessage]);

  // ── Mute helper ──────────────────────────────────────────────────────────────

  const handleToggleMute = useCallback(async () => {
    if (!selectedChatId) return;
    try {
      await toggleMuteChat(selectedChatId);
      const isMuted = (chat as any)?.is_muted;
      toast.success(isMuted ? 'Notifiche riattivate' : 'Chat silenziata');
    } catch {
      toast.error('Errore');
    }
  }, [selectedChatId, chat, toggleMuteChat]);

  // ── Reaction picker helpers ─────────────────────────────────────────────────

  const openReactionPicker = useCallback((msgId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setReactionPickerMsgId(msgId);
    setReactionPickerPos({ x: rect.left, y: rect.top });
  }, []);

  // ── Rileva provider meeting disponibili (Google Meet / Microsoft Teams) ──────
  useEffect(() => {
    if (!isOpen || !userProfile?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('google_account_connected, google_workspace_scopes, microsoft_account_connected')
        .eq('id', userProfile.id)
        .single();
      if (cancelled || !data) return;
      const scopes: string[] = (data as any).google_workspace_scopes || [];
      setMeetGoogleAvailable(
        !!(data as any).google_account_connected &&
        scopes.includes('https://www.googleapis.com/auth/calendar')
      );
      setMeetMicrosoftConnected(!!(data as any).microsoft_account_connected);
    })();
    return () => { cancelled = true; };
  }, [isOpen, userProfile?.id]);

  // ── Meeting handler (Google Meet / Microsoft Teams) ─────────────────────────
  const handleCreateMeet = useCallback(async (provider: MeetProvider = 'google_meet') => {
    if (!selectedChatId || !userProfile?.id || creatingMeet) return;

    setCreatingMeet(true);
    try {
      const chatName = selectedChat?.name || 'Team Chat';
      const now = new Date();
      const end = new Date(now.getTime() + 3600000); // +1 ora

      // ── Microsoft Teams: evento Outlook dell'utente corrente con meeting Teams ──
      if (provider === 'microsoft_teams') {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('microsoft_account_connected')
          .eq('id', userProfile.id)
          .single();

        if (!profile?.microsoft_account_connected) {
          toast.error('Collega il tuo account Microsoft nelle impostazioni per creare un meeting Teams');
          return;
        }

        const { createMicrosoftEvent } = useCalendarStore.getState();
        const result = await createMicrosoftEvent(
          undefined,
          {
            title: `Meet: ${chatName}`,
            start_time: now.toISOString(),
            end_time: end.toISOString(),
            all_day: false,
            description: `Riunione creata dalla team chat "${chatName}"`,
            attendees: [],
          } as any,
          { createTeamsMeeting: true },
        );

        const joinUrl = result?.joinUrl;
        if (!joinUrl) {
          toast.error('Meeting Teams creato ma link non disponibile');
          return;
        }

        const { sendMessage } = useTeamChatStore.getState();
        await sendMessage(selectedChatId, `📹 Microsoft Teams: ${joinUrl}`);
        toast.success('Link Microsoft Teams inviato!');
        return;
      }

      // ── Google Meet (comportamento esistente) ──
      // Verifica se l'utente ha Google connesso
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('google_account_connected, google_workspace_scopes, organization_id, email')
        .eq('id', userProfile.id)
        .single();

      if (!profile?.google_account_connected) {
        toast.error('Collega il tuo account Google nelle impostazioni per creare un Meet');
        return;
      }

      const scopes: string[] = profile.google_workspace_scopes || [];
      if (!scopes.includes('https://www.googleapis.com/auth/calendar')) {
        toast.error('Serve il permesso Google Calendar per creare un Meet');
        return;
      }

      const { data, error } = await supabase.functions.invoke('google-calendar-modify', {
        body: {
          operation: 'create',
          organization_id: profile.organization_id,
          calendar_id: profile.email, // Calendar primario dell'utente
          assigned_user_id: userProfile.id,
          event: {
            title: `Meet: ${chatName}`,
            start_time: now.toISOString(),
            end_time: end.toISOString(),
            description: `Riunione creata dalla team chat "${chatName}"`,
            conference_data: {
              createRequest: {
                requestId: crypto.randomUUID(),
                conferenceSolutionKey: { type: 'hangoutsMeet' },
              },
            },
          },
        },
      });

      if (error) throw error;

      // Estrai il link Meet dalla risposta
      const meetUri = data?.event?.conferenceData?.entryPoints?.find(
        (ep: any) => ep.entryPointType === 'video'
      )?.uri;

      if (!meetUri) {
        toast.error('Meet creato ma link non disponibile');
        return;
      }

      // Invia il link come messaggio nella chat
      const { sendMessage } = useTeamChatStore.getState();
      await sendMessage(selectedChatId, `📹 Google Meet: ${meetUri}`);
      toast.success('Link Google Meet inviato!');
    } catch (err: any) {
      console.error('Errore creazione Meet:', err);
      toast.error(err?.message || 'Errore nella creazione del Meet');
    } finally {
      setCreatingMeet(false);
    }
  }, [selectedChatId, userProfile?.id, selectedChat?.name, creatingMeet]);

  // Click sul bottone meeting: sceglie il provider o apre il menu se entrambi disponibili
  const handleMeetButtonClick = useCallback(() => {
    if (creatingMeet) return;
    if (meetGoogleAvailable && teamsMeetAvailable) {
      setShowMeetMenu((v) => !v);
      return;
    }
    if (teamsMeetAvailable && !meetGoogleAvailable) {
      handleCreateMeet('microsoft_teams');
      return;
    }
    // Google disponibile OPPURE nessuno dei due (fallback: mostra il toast Google esistente)
    handleCreateMeet('google_meet');
  }, [creatingMeet, meetGoogleAvailable, teamsMeetAvailable, handleCreateMeet]);

  // ── Rich media handlers ─────────────────────────────────────────────────────

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || !selectedChatId) return;
    const oversized = Array.from(files).filter(f => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      toast.error(`File troppo grandi (max 10 MB): ${oversized.map(f => f.name).join(', ')}`);
      if (oversized.length === files.length) return;
    }

    const newStaged: StagedFile[] = [];
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) continue;
      const isImage = file.type.startsWith('image/');
      const preview = isImage ? URL.createObjectURL(file) : null;
      const attachmentType: MessageAttachment['type'] = isImage ? 'image'
        : file.type.startsWith('video/') ? 'video'
        : file.type.startsWith('audio/') ? 'audio'
        : 'file';
      newStaged.push({ id: crypto.randomUUID(), file, preview, attachmentType });
    }
    setStagedFiles(prev => [...prev, ...newStaged]);
    // Focus sull'input per scrivere la caption
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [selectedChatId, inputRef]);

  const removeStagedFile = useCallback((id: string) => {
    setStagedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
  }, []);

  const handleAudioSend = useCallback(async (blob: Blob, durationSec: number) => {
    if (!selectedChatId) return;
    setUploading(true);
    try {
      const file = new File([blob], `audio-${Date.now()}.webm`, { type: blob.type });
      const att = await uploadChatFile(file, selectedChatId);
      att.duration = durationSec;
      setIsRecording(false);
      // Invia subito il messaggio audio
      const { sendMessage } = useTeamChatStore.getState();
      await sendMessage(selectedChatId, '', null, [att], 'audio');
    } catch {
      toast.error("Errore nell'invio dell'audio");
    } finally {
      setUploading(false);
    }
  }, [selectedChatId]);

  const handleSendWithAttachments = useCallback(async () => {
    if (!selectedChatId) return;
    const hasText = inputValue.trim().length > 0;
    const hasFiles = stagedFiles.length > 0;
    if (!hasText && !hasFiles) return;

    if (hasFiles) {
      const chatId = selectedChatId;
      const caption = inputValue.trim();
      const filesToUpload = [...stagedFiles];
      setStagedFiles([]);
      // Pulisci l'input testo
      handleInputChange('');

      for (const staged of filesToUpload) {
        const uploadId = staged.id;
        setUploadingItems(prev => [...prev, {
          id: uploadId,
          name: staged.file.name,
          size: staged.file.size,
          attachmentType: staged.attachmentType,
          preview: staged.preview,
          progress: 0,
        }]);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

        uploadChatFile(staged.file, chatId, (pct) => {
          setUploadingItems(prev => prev.map(u => u.id === uploadId ? { ...u, progress: pct } : u));
        }).then(async (att) => {
          if (att.type === 'image' && att.url) {
            await new Promise<void>(resolve => {
              const img = new window.Image();
              img.onload = img.onerror = () => resolve();
              img.src = att.url;
            });
          }
          const { sendMessage } = useTeamChatStore.getState();
          // Solo il primo file porta la caption
          const isFirst = filesToUpload[0].id === uploadId;
          await sendMessage(chatId, isFirst ? caption : '', null, [att], att.type);
          setUploadingItems(prev => prev.filter(u => u.id !== uploadId));
          if (staged.preview) URL.revokeObjectURL(staged.preview);
        }).catch(() => {
          setUploadingItems(prev => prev.filter(u => u.id !== uploadId));
          if (staged.preview) URL.revokeObjectURL(staged.preview);
          toast.error(`Errore nel caricamento di ${staged.file.name}`);
        });
      }
    } else {
      handleSend();
    }
  }, [selectedChatId, inputValue, stagedFiles, handleSend, handleInputChange, messagesEndRef]);

  return (
    <div
      className={`fixed top-0 sm:top-12 bottom-0 right-0 w-full md:w-[60%] bg-white z-[64] flex transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      style={{
        boxShadow: 'rgba(55, 53, 47, 0.08) -4px 0px 16px, rgba(55, 53, 47, 0.04) -8px 0px 32px'
      }}
    >
      {/* Left: Chat List - Notion sidebar style */}
      <div
        className="w-full md:w-64 flex flex-col flex-shrink-0"
        style={{ backgroundColor: '#F7F7F5', borderRight: '1px solid rgba(55, 53, 47, 0.09)' }}
      >
        <div
          className="px-3 py-2"
          style={{ borderBottom: '1px solid rgba(55, 53, 47, 0.09)' }}
        >
          <div className="relative flex items-center">
            <Search className="absolute left-2.5 w-3.5 h-3.5 pointer-events-none" style={{ color: '#9B9A97' }} strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Cerca..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md outline-none"
              style={{
                backgroundColor: 'rgba(55,53,47,0.06)',
                color: '#37352F',
                border: '1px solid transparent',
                fontSize: '13px'
              }}
              onFocus={(e) => (e.currentTarget.style.border = '1px solid rgba(35,131,226,0.4)')}
              onBlur={(e) => (e.currentTarget.style.border = '1px solid transparent')}
            />
          </div>
        </div>
        {/* Bottone messaggi salvati */}
        <button
          onClick={() => setShowSavedPanel(s => !s)}
          className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] transition-colors"
          style={{ color: showSavedPanel ? '#2383E2' : '#787774', borderBottom: '1px solid rgba(55,53,47,0.06)' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <Bookmark className="w-3.5 h-3.5" strokeWidth={1.5} />
          Messaggi salvati
        </button>
        {/* Stato personalizzato */}
        <div className="relative" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
          <button
            onClick={() => setShowStatusPopover(s => !s)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] transition-colors"
            style={{ color: '#787774' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <span className="text-sm">{myStatus?.emoji || '😊'}</span>
            <span className="truncate">{myStatus?.text || 'Imposta stato'}</span>
          </button>
          {showStatusPopover && <CustomStatusPopover onClose={() => setShowStatusPopover(false)} />}
        </div>
        <div className="flex-1 overflow-y-auto">
          {showSavedPanel ? (
            <SavedMessagesPanel
              onClose={() => setShowSavedPanel(false)}
              onNavigateToMessage={(chatId, messageId) => {
                setShowSavedPanel(false);
                selectChat(chatId).then(() => handleNavigateToMessage(messageId));
              }}
            />
          ) : loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#2383E2' }} />
            </div>
          ) : filteredChats.length === 0 && searchQuery ? (
            <p className="text-xs px-3 py-4 text-center" style={{ color: '#9B9A97' }}>Nessuna chat trovata</p>
          ) : (
            filteredChats.map(c => {
              const isActive = c.id === selectedChatId;
              const name = getChatDisplayName(c);
              const avatar = getChatAvatar(c);
              const unread = c.unread_count || 0;
              const isMenuOpen = menuOpenId === c.id;
              const isCreator = c.created_by === userProfile?.id;

              // Avatar composito per gruppi: prime 2 immagini partecipanti
              const groupParticipants = c.is_group ? (c.participants || []).filter(p => p.user_id !== userProfile?.id) : [];

              const openMenu = (x: number, y: number) => {
                setMenuPos({ top: y, left: Math.min(x, window.innerWidth - 188) });
                setMenuOpenId(c.id);
              };

              return (
                <div key={c.id} className="relative group">
                  <button
                    onClick={() => { selectChat(c.id); setSummaryData(null); }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isMenuOpen) { closeMenu(); return; }
                      openMenu(e.clientX, e.clientY);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors"
                    style={{ backgroundColor: isActive ? 'rgba(35, 131, 226, 0.1)' : 'transparent' }}
                    onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'rgba(55, 53, 47, 0.04)')}
                    onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    {/* Avatar */}
                    {c.chat_type === 'visitor' ? (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-100">
                        <Globe className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
                      </div>
                    ) : c.is_group ? (
                      <div className="w-8 h-8 relative flex-shrink-0">
                        {groupParticipants.slice(0, 2).map((p, idx) => (
                          p.user?.profile_image_url ? (
                            <img key={p.user_id} src={p.user.profile_image_url} alt={p.user.full_name}
                              className="w-5 h-5 rounded-full object-cover absolute border border-white"
                              style={{ top: idx === 0 ? 0 : 'auto', bottom: idx === 1 ? 0 : 'auto', left: idx === 0 ? 0 : 'auto', right: idx === 1 ? 0 : 'auto' }} />
                          ) : (
                            <div key={p.user_id}
                              className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-semibold absolute border border-white"
                              style={{ backgroundColor: 'rgba(35,131,226,0.15)', color: '#2383E2', top: idx === 0 ? 0 : 'auto', bottom: idx === 1 ? 0 : 'auto', left: idx === 0 ? 0 : 'auto', right: idx === 1 ? 0 : 'auto' }}>
                              {getInitials(p.user?.full_name)}
                            </div>
                          )
                        ))}
                        {groupParticipants.length === 0 && (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(35,131,226,0.1)' }}>
                            <Users className="w-4 h-4" style={{ color: '#2383E2' }} strokeWidth={1.5} />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="relative flex-shrink-0">
                        {avatar ? (
                          <img src={avatar} alt={name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
                            style={{ backgroundColor: 'rgba(55, 53, 47, 0.08)', color: '#37352F' }}>
                            {getInitials(name)}
                          </div>
                        )}
                        {/* Pallino presenza per chat dirette */}
                        {!c.is_group && (() => {
                          const other = c.participants?.find(p => p.user_id !== userProfile?.id);
                          return other ? (
                            <PresenceDot
                              userId={other.user_id}
                              size={8}
                              borderWidth={1.5}
                              className="absolute -bottom-0.5 -right-0.5"
                            />
                          ) : null;
                        })()}
                      </div>
                    )}

                    {/* Testo: nome + anteprima */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-1">
                        <span className="flex items-center gap-1.5 min-w-0">
                          <span className={`text-[13px] truncate ${unread > 0 || isActive ? 'font-semibold' : 'font-medium'}`}
                            style={{ color: isActive ? '#2383E2' : '#37352F' }}>
                            {name}
                          </span>
                          {c.chat_type === 'visitor' && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-700 flex-shrink-0">
                              <Globe className="w-2.5 h-2.5" strokeWidth={2} />
                              Visitor
                            </span>
                          )}
                        </span>
                        {c.last_message && (
                          <span className="text-[10px] flex-shrink-0" style={{ color: '#9B9A97' }}>
                            {formatChatTime(c.last_message.created_at)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-[11px] truncate"
                          style={{ color: unread > 0 ? '#37352F' : '#9B9A97', fontWeight: unread > 0 ? 500 : 400 }}>
                          {c.chat_type === 'visitor' && !c.last_message ? (
                            (() => {
                              if (!c.visitor?.last_seen_at) return c.visitor?.visitor_email || 'Visitor';
                              const diffMs = Date.now() - new Date(c.visitor.last_seen_at).getTime();
                              if (diffMs < 60_000) return (<><span className="text-emerald-600">●</span> Online</>);
                              return `Offline · ${formatDistanceToNow(new Date(c.visitor.last_seen_at), { locale: it })} fa`;
                            })()
                          ) : (() => {
                            const content = c.last_message?.content || '';
                            if (c.last_message?.message_type === 'whatsapp_forward') {
                              try { return `📋 ${JSON.parse(content).title || 'Richiesta da WhatsApp'}`; } catch { return '📋 Richiesta da WhatsApp'; }
                            }
                            if (c.last_message?.message_type === 'task_comment') {
                              try { const p = JSON.parse(content); return `💬 ${p.author_name}: ${p.comment_content?.substring(0, 50) || 'commento su task'}`; } catch { return '💬 Commento su task'; }
                            }
                            if (c.last_message?.message_type === 'crm_share') {
                              try { const p = JSON.parse(content); const labels: Record<string, string> = { lead: '👤', task: '✅', project: '📁', deal: '💰' }; return `${labels[p.entity_type] || '📎'} ${p.entity_data?.name || p.entity_data?.title || 'Condivisione'}`; } catch { return '📎 Condivisione'; }
                            }
                            // Rileva JSON strutturato (whatsapp forward non taggato correttamente)
                            if (content.startsWith('{') && content.includes('"request_type"')) {
                              try { return `📋 ${JSON.parse(content).title || 'Richiesta da WhatsApp'}`; } catch { /* fallthrough */ }
                            }
                            if (content.startsWith('{') && content.includes('"task_id"') && content.includes('"comment_content"')) {
                              try { const p = JSON.parse(content); return `💬 ${p.author_name}: ${p.comment_content?.substring(0, 50) || 'commento su task'}`; } catch { /* fallthrough */ }
                            }
                            // Attachment preview
                            const mt = c.last_message?.message_type;
                            if (mt === 'image') return content ? `📷 ${content}` : '📷 Immagine';
                            if (mt === 'video') return content ? `🎬 ${content}` : '🎬 Video';
                            if (mt === 'audio') return '🎤 Audio';
                            if (mt === 'file') return content ? `📎 ${content}` : '📎 File';
                            if (!content && c.last_message?.attachments?.length) {
                              const a = (c.last_message.attachments as any[])[0];
                              if (a?.type === 'image') return '📷 Immagine';
                              if (a?.type === 'video') return '🎬 Video';
                              if (a?.type === 'audio') return '🎤 Audio';
                              return '📎 File';
                            }
                            return content;
                          })()}
                        </p>
                        {unread > 0 && !isMenuOpen && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {c.has_unread_mention && (
                              <span className="min-w-[16px] h-[16px] px-1 text-white text-[9px] font-bold rounded-full flex items-center justify-center"
                                style={{ backgroundColor: '#2383E2' }}>
                                @
                              </span>
                            )}
                            <span className="min-w-[16px] h-[16px] px-1 text-white text-[9px] font-semibold rounded-full flex items-center justify-center"
                              style={{ backgroundColor: '#2383E2' }}>
                              {unread}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>

                </div>
              );
            })
          )}
        </div>

        {/* Sezione chat archiviate */}
        <div style={{ borderTop: '1px solid rgba(55, 53, 47, 0.09)' }}>
          <button
            onClick={() => {
              if (!showArchived) fetchArchivedChats();
              setShowArchived(v => !v);
            }}
            className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors"
            style={{ color: '#787774' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            {showArchived
              ? <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
              : <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />}
            Archiviate {archivedChats.length > 0 && `(${archivedChats.length})`}
          </button>

          {showArchived && (
            <div className="max-h-40 overflow-y-auto">
              {archivedChats.length === 0 ? (
                <p className="text-xs px-3 py-2" style={{ color: '#9B9A97' }}>Nessuna chat archiviata</p>
              ) : (
                archivedChats.map(c => {
                  const name = getChatDisplayName(c);
                  const avatar = getChatAvatar(c);
                  return (
                    <div key={c.id} className="flex items-center gap-2 px-3 py-2 group">
                      {c.is_group ? (
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(55,53,47,0.08)' }}>
                          <Users className="w-3 h-3" style={{ color: '#787774' }} strokeWidth={1.5} />
                        </div>
                      ) : avatar ? (
                        <img src={avatar} alt={name} className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium flex-shrink-0" style={{ backgroundColor: 'rgba(55,53,47,0.08)', color: '#37352F' }}>
                          {getInitials(name)}
                        </div>
                      )}
                      <span className="text-xs truncate flex-1" style={{ color: '#9B9A97' }}>{name}</span>
                      <button
                        onClick={async () => {
                          try {
                            await restoreChat(c.id);
                            toast.success('Chat ripristinata');
                          } catch {
                            toast.error('Errore nel ripristino');
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity"
                        style={{ color: '#787774' }}
                        title="Ripristina"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Menu contestuale chat — Portal su document.body per evitare stacking context del transform */}
      {menuOpenId && menuPos && (() => {
        const menuChat = chats.find(c => c.id === menuOpenId);
        const isCreator = menuChat?.created_by === userProfile?.id;
        return createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={closeMenu} />
            <div
              className="fixed z-[9999] bg-white rounded-lg py-1 w-44"
              style={{ top: menuPos.top, left: menuPos.left, border: '1px solid rgba(55,53,47,0.09)', boxShadow: 'rgba(55,53,47,0.1) 0px 4px 16px' }}
            >
              {/* Segna come non letta (solo se già letta) */}
              {(menuChat?.unread_count || 0) === 0 && (
                <button
                  onClick={() => {
                    markChatAsUnread(menuOpenId!);
                    closeMenu();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                  style={{ color: '#37352F' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <EyeOff className="w-4 h-4" strokeWidth={1.5} style={{ color: '#787774' }} />
                  Segna come non letta
                </button>
              )}
              <button
                onClick={async () => {
                  const id = menuOpenId;
                  closeMenu();
                  if (!confirm('Uscire da questa chat?')) return;
                  try { await leaveChat(id); toast.success('Sei uscito dalla chat'); }
                  catch { toast.error("Errore nell'uscita dalla chat"); }
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                style={{ color: '#37352F' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <LogOut className="w-4 h-4" strokeWidth={1.5} style={{ color: '#787774' }} />
                Esci dalla chat
              </button>
              {isCreator && (
                <button
                  onClick={async () => {
                    const id = menuOpenId;
                    closeMenu();
                    if (!confirm('Archiviare questa chat? Non sarà più visibile.')) return;
                    try { await archiveChat(id); toast.success('Chat archiviata'); }
                    catch { toast.error("Errore nell'archiviazione della chat"); }
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                  style={{ color: '#37352F' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <Archive className="w-4 h-4" strokeWidth={1.5} style={{ color: '#787774' }} />
                  Archivia chat
                </button>
              )}
            </div>
          </>,
          document.body
        );
      })()}

      {/* AddMembersModal */}
      {chat && showAddMembersModal && (
        <AddMembersModal
          isOpen={showAddMembersModal}
          chat={chat}
          onClose={() => setShowAddMembersModal(false)}
        />
      )}

      {/* Image Lightbox */}
      {lightboxImages && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxImages(null)}
        />
      )}

      {/* Forward Message Modal */}
      {forwardingMessage && (
        <ForwardMessageModal
          message={forwardingMessage}
          onClose={() => setForwardingMessage(null)}
        />
      )}

      {/* Emoji picker portal (per input) */}
      {showEmojiPicker && (() => {
        const anchor = emojiPickerAnchorRef.current?.getBoundingClientRect();
        return createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setShowEmojiPicker(false)} />
            <div
              className="fixed z-[9999]"
              style={{
                bottom: anchor ? window.innerHeight - anchor.top + 8 : 80,
                left: anchor ? anchor.left : 16,
              }}
            >
              <SimpleEmojiPicker onSelect={(e) => { insertEmoji(e); setShowEmojiPicker(false); }} />
            </div>
          </>,
          document.body
        );
      })()}

      {/* Quick reaction picker portal */}
      {reactionPickerMsgId && reactionPickerPos && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setReactionPickerMsgId(null)} />
          <div
            className="fixed z-[9999] flex items-center gap-0.5 p-1.5 rounded-full bg-white shadow-xl"
            style={{
              top: reactionPickerPos.y,
              left: reactionPickerPos.x,
              transform: 'translateY(-100%) translateY(-6px)',
              border: '1px solid rgba(55,53,47,0.12)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {QUICK_REACTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => {
                  if (reactionPickerMsgId && selectedChatId) {
                    addReaction(reactionPickerMsgId, selectedChatId, emoji);
                  }
                  setReactionPickerMsgId(null);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-full text-lg transition-transform hover:scale-125"
                style={{ color: 'initial' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                {emoji}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}

      {/* Right: Messages + optional panels */}
      <div className="flex-1 flex flex-row bg-white min-w-0">
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header - Notion style */}
        <div
          className="flex items-center justify-between px-4 py-3 bg-white"
          style={{ borderBottom: '1px solid rgba(55, 53, 47, 0.09)' }}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1 group/header">
            {chat?.is_group && <Users className="w-4 h-4 flex-shrink-0 text-[#787774]" strokeWidth={1.5} />}
            {isRenaming && chat?.is_group ? (
              <input
                autoFocus
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter') {
                    try { await renameChat(chat.id, renameValue); } catch { toast.error('Errore nel rinominare'); }
                    setIsRenaming(false);
                  } else if (e.key === 'Escape') {
                    setIsRenaming(false);
                  }
                }}
                onBlur={async () => {
                  if (renameValue.trim() && chat) {
                    try { await renameChat(chat.id, renameValue); } catch { toast.error('Errore nel rinominare'); }
                  }
                  setIsRenaming(false);
                }}
                className="font-semibold text-[#37352F] bg-transparent border-b border-[#2383E2] outline-none truncate min-w-0"
                style={{ maxWidth: 200 }}
              />
            ) : (
              <>
                <span className="font-semibold truncate text-[#37352F]">{chat ? getChatDisplayName(chat) : 'Chat Team'}</span>
                {chat?.is_group && (
                  <button
                    onClick={() => { setRenameValue(getChatDisplayName(chat)); setIsRenaming(true); }}
                    className="opacity-0 group-hover/header:opacity-100 transition-opacity p-0.5 rounded flex-shrink-0"
                    style={{ color: '#787774' }}
                    title="Rinomina gruppo"
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </>
            )}
            {/* Action buttons — left side, after name */}
            {chat && (chat.participants?.length ?? 0) > 0 && (
              <button
                onClick={() => chat.is_group && setShowParticipantsList(s => !s)}
                className="flex items-center cursor-pointer hover:opacity-80 transition-opacity ml-2"
                title={chat.is_group ? 'Gestisci partecipanti' : 'Partecipanti'}
              >
                {(chat.participants || []).slice(0, 4).map((p, idx) => (
                  <div key={p.user_id} className="relative" style={{ marginLeft: idx === 0 ? 0 : -8, zIndex: idx }}>
                    {p.user?.profile_image_url ? (
                      <img
                        src={p.user.profile_image_url}
                        alt={p.user.full_name}
                        title={p.user.full_name}
                        className="w-6 h-6 rounded-full object-cover border-2 border-white"
                      />
                    ) : (
                      <div
                        title={p.user?.full_name}
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold border-2 border-white"
                        style={{ backgroundColor: 'rgba(55,53,47,0.12)', color: '#37352F' }}
                      >
                        {getInitials(p.user?.full_name)}
                      </div>
                    )}
                    <PresenceDot
                      userId={p.user_id}
                      size={7}
                      borderWidth={1}
                      className="absolute -bottom-0.5 -right-0.5"
                    />
                  </div>
                ))}
                {(chat.participants?.length ?? 0) > 4 && (
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold border-2 border-white"
                    style={{ backgroundColor: 'rgba(55,53,47,0.12)', color: '#37352F', marginLeft: -8 }}
                  >
                    +{(chat.participants?.length ?? 0) - 4}
                  </div>
                )}
              </button>
            )}
            <button
              onClick={() => setShowPinnedPanel(s => !s)}
              className="p-1.5 rounded transition-colors"
              style={{ color: showPinnedPanel ? '#2383E2' : '#787774' }}
              title="Messaggi fissati"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Pin className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => setShowMessageSearch(s => !s)}
              className="p-1.5 rounded transition-colors"
              style={{ color: showMessageSearch ? '#2383E2' : '#787774' }}
              title="Cerca nei messaggi"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Search className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button
              onClick={handleToggleMute}
              className="p-1.5 rounded transition-colors"
              style={{ color: (chat as any)?.is_muted ? '#2383E2' : '#787774' }}
              title={(chat as any)?.is_muted ? 'Riattiva notifiche' : 'Silenzia chat'}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {(chat as any)?.is_muted ? <BellOff className="w-4 h-4" strokeWidth={1.5} /> : <Bell className="w-4 h-4" strokeWidth={1.5} />}
            </button>
            {chat?.is_group && (
              <button
                onClick={() => setShowAddMembersModal(true)}
                className="p-1.5 rounded transition-colors"
                style={{ color: '#787774' }}
                title="Aggiungi membro"
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <UserPlus className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
            <button
              onClick={onNewChat}
              className="p-1.5 rounded transition-colors"
              style={{ color: '#787774' }}
              title="Nuova chat"
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          {/* Close button — right side */}
          <button
            onClick={onClose}
            className="p-1.5 rounded transition-colors flex-shrink-0"
            style={{ color: '#787774' }}
            title="Chiudi chat"
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Visitor banner — solo per chat_type='visitor' */}
        {chat?.chat_type === 'visitor' && chat.visitor && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-emerald-50 border-b border-emerald-200">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-emerald-100 flex-shrink-0">
                <Globe className="w-4 h-4 text-emerald-600" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-emerald-900 truncate">
                  {chat.visitor.visitor_name || chat.visitor.visitor_email || 'Visitor'}
                </div>
                <div className="text-[11px] text-emerald-700 truncate flex items-center gap-2">
                  {chat.visitor.visitor_email && (
                    <span className="truncate">{chat.visitor.visitor_email}</span>
                  )}
                  {chat.visitor.last_seen_at && (
                    <span className="flex-shrink-0">
                      · Ultima attività {formatDistanceToNow(new Date(chat.visitor.last_seen_at), { addSuffix: true, locale: it })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {chat.visitor.lead_id && (
              <button
                onClick={() => { navigate(`/leads/${chat.visitor!.lead_id}`); onClose(); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[12px] font-medium bg-white text-emerald-700 hover:bg-emerald-100 border border-emerald-300 transition-colors flex-shrink-0"
                title="Apri il lead collegato"
              >
                <ExternalLink className="w-3 h-3" strokeWidth={2} />
                Vedi lead
              </button>
            )}
          </div>
        )}

        {/* Message search bar */}
        {showMessageSearch && selectedChatId && (
          <MessageSearchBar
            chatId={selectedChatId}
            onClose={() => { setShowMessageSearch(false); setHighlightedMessageId(null); }}
            onNavigateToMessage={handleNavigateToMessage}
          />
        )}

        {/* Participants list panel */}
        {showParticipantsList && chat?.is_group && (
          <div
            className="px-4 py-3 space-y-1 overflow-y-auto max-h-48"
            style={{ borderBottom: '1px solid rgba(55,53,47,0.09)', backgroundColor: '#FBFBFA' }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-semibold" style={{ color: '#37352F' }}>
                Partecipanti ({chat.participants?.length || 0})
              </span>
              <button
                onClick={() => setShowParticipantsList(false)}
                className="p-0.5 rounded hover:bg-[rgba(55,53,47,0.06)]"
              >
                <X className="w-3.5 h-3.5" style={{ color: '#787774' }} strokeWidth={1.5} />
              </button>
            </div>
            {(chat.participants || []).map(p => {
              const isMe = p.user_id === userProfile?.id;
              const isCreator = chat.created_by === p.user_id;
              return (
                <div
                  key={p.user_id}
                  className="flex items-center gap-2.5 py-1.5 px-1 rounded-lg group/participant"
                  style={{ transition: 'background-color 150ms' }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.04)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div className="relative flex-shrink-0">
                    {p.user?.profile_image_url ? (
                      <img src={p.user.profile_image_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ backgroundColor: 'rgba(55,53,47,0.12)', color: '#37352F' }}>
                        {getInitials(p.user?.full_name)}
                      </div>
                    )}
                    <PresenceDot userId={p.user_id} size={7} borderWidth={1} className="absolute -bottom-0.5 -right-0.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[13px] truncate block" style={{ color: '#37352F', fontWeight: isMe ? 600 : 500 }}>
                      {p.user?.full_name || 'Utente'}{isMe ? ' (tu)' : ''}
                    </span>
                    {isCreator && (
                      <span className="text-[10px]" style={{ color: '#9B9A97' }}>Creatore</span>
                    )}
                  </div>
                  {/* Bottone rimuovi — non per se stessi e non per il creatore */}
                  {!isMe && !isCreator && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await removeParticipant(chat.id, p.user_id);
                          toast.success(`${p.user?.full_name || 'Utente'} rimosso dal gruppo`);
                        } catch {
                          toast.error('Errore nella rimozione');
                        }
                      }}
                      className="hidden group-hover/participant:flex p-1 rounded hover:bg-red-50 transition-colors"
                      title={`Rimuovi ${p.user?.full_name}`}
                    >
                      <X className="w-3.5 h-3.5 text-red-500" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Messages area */}
        {!chat ? (
          <div className="flex-1 flex items-center justify-center" style={{ color: '#787774' }}>
            Seleziona una chat
          </div>
        ) : (
          <>
            <div
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4 relative"
              style={{
                backgroundColor: '#FFFFFF',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23e0e0e0' fill-opacity='0.12'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false); }}
              onDrop={e => {
                e.preventDefault();
                setIsDragging(false);
                handleFileSelect(e.dataTransfer.files);
              }}
            >
              {/* Drag overlay */}
              {isDragging && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-xl pointer-events-none"
                  style={{ backgroundColor: 'rgba(35,131,226,0.08)', border: '2px dashed #2383E2' }}>
                  <Paperclip className="w-8 h-8 mb-2" style={{ color: '#2383E2' }} strokeWidth={1.5} />
                  <p className="text-sm font-medium" style={{ color: '#2383E2' }}>Rilascia per allegare</p>
                </div>
              )}
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#2383E2' }} />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex items-center justify-center h-full" style={{ color: '#787774' }}>
                  Nessun messaggio
                </div>
              ) : (
                <>
                {loadingOlderMessages && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#787774' }} strokeWidth={1.5} />
                  </div>
                )}
                {messages.map((msg, msgIdx) => {
                  const isOwn = msg.sender_id === userProfile?.id;
                  const isEditing = editingId === msg.id;
                  // Visitor / system message detection (sender_id null + metadata.sender_type)
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const senderType = (msg.metadata as any)?.sender_type as string | undefined;
                  const isVisitorMsg = msg.sender_id === null && senderType === 'visitor';
                  const isSystemMsg = msg.sender_id === null && senderType === 'system';

                  // Timestamp grouping: mostra l'ora solo sull'ultimo messaggio consecutivo dello stesso sender (entro 2 min)
                  const nextMsg = msgIdx < messages.length - 1 ? messages[msgIdx + 1] : null;
                  const showTimestamp = !nextMsg
                    || nextMsg.sender_id !== msg.sender_id
                    || (new Date(nextMsg.created_at).getTime() - new Date(msg.created_at).getTime()) > 120000;

                  // Sender name grouping: nascondi nome se il messaggio precedente è dello stesso sender (entro 2 min)
                  const prevMsg = msgIdx > 0 ? messages[msgIdx - 1] : null;
                  const showSenderName = !prevMsg
                    || prevMsg.sender_id !== msg.sender_id
                    || (new Date(msg.created_at).getTime() - new Date(prevMsg.created_at).getTime()) > 120000;

                  // Mostra il summary card subito dopo il messaggio coperto dall'ultimo riassunto
                  // Se lastMessageId non matcha nessun msg visibile, mostra dopo l'ultimo messaggio
                  const isLastMsg = msgIdx === messages.length - 1;
                  const lastIdMatchesAny = summaryData?.lastMessageId && messages.some(m => m.id === summaryData.lastMessageId);
                  const showSummaryAfterThis = summaryData && (
                    summaryData.lastMessageId === msg.id ||
                    (isLastMsg && !lastIdMatchesAny)
                  );

                  // System message (sender_id null + sender_type='system'): render compatto centrato/italico
                  if (isSystemMsg) {
                    return (
                      <div
                        key={msg.id}
                        id={`msg-${msg.id}`}
                        className="flex justify-center my-2"
                      >
                        <div className="text-[11px] italic text-center text-[#787774] bg-[rgba(55,53,47,0.04)] rounded-full px-3 py-1 max-w-[80%]">
                          {msg.content}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <React.Fragment key={msg.id}>
                    <div
                      id={`msg-${msg.id}`}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group transition-colors duration-500`}
                      style={{ backgroundColor: highlightedMessageId === msg.id ? 'rgba(35,131,226,0.12)' : 'transparent', borderRadius: 12, marginTop: showSenderName ? 12 : 2 }}
                    >
                      <div className="max-w-[75%] relative">
                        {/* Action icons - floating above bubble on hover (nascoste per messaggi temp) */}
                        <div
                          className={`${msg._status ? 'hidden' : 'hidden group-hover:flex'} items-center gap-0.5 absolute -top-7 ${isOwn ? 'right-0' : 'left-0'} z-10 bg-white rounded-lg px-1.5 py-1 text-[10px]`}
                          style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.06)', color: '#667781' }}
                        >
                          <button onClick={(e) => openReactionPicker(msg.id, e)} className="p-0.5 rounded hover:bg-gray-100 transition-colors" title="Reagisci"><Smile className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                          <button onClick={() => handleReply(msg)} className="p-0.5 rounded hover:bg-gray-100 transition-colors" title="Rispondi"><Reply className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                          <button onClick={() => handleCopyMessage(msg)} className="p-0.5 rounded hover:bg-gray-100 transition-colors" title="Copia"><Copy className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                          <button onClick={() => setForwardingMessage(msg)} className="p-0.5 rounded hover:bg-gray-100 transition-colors" title="Inoltra"><Forward className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                          <button onClick={() => handleTogglePin(msg)} className="p-0.5 rounded hover:bg-gray-100 transition-colors" title={msg.is_pinned ? 'Rimuovi pin' : 'Fissa'}>
                            <Pin className="w-3.5 h-3.5" strokeWidth={1.5} style={msg.is_pinned ? { color: '#2383E2' } : undefined} />
                          </button>
                          <button
                            onClick={() => savedMessageIds.has(msg.id) ? unsaveMessage(msg.id) : saveMessage(msg.id, msg.chat_id)}
                            className="p-0.5 rounded hover:bg-gray-100 transition-colors"
                            title={savedMessageIds.has(msg.id) ? 'Rimuovi dai salvati' : 'Salva'}
                          >
                            <Bookmark className="w-3.5 h-3.5" strokeWidth={1.5} style={savedMessageIds.has(msg.id) ? { color: '#2383E2', fill: '#2383E2' } : undefined} />
                          </button>
                          {isOwn && (
                            <>
                              <button onClick={() => startEditing(msg)} className="p-0.5 rounded hover:bg-gray-100 transition-colors" title="Modifica"><Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                              <button onClick={() => handleDelete(msg.id)} className="p-0.5 rounded hover:bg-red-50 transition-colors" title="Elimina"><Trash2 className="w-3.5 h-3.5 text-red-500" strokeWidth={1.5} /></button>
                            </>
                          )}
                        </div>
                        {!isOwn && (chat.is_group || isVisitorMsg) && showSenderName && (
                          <div className="text-[11px] mb-0.5 ml-1 flex items-center gap-1" style={{ color: '#787774' }}>
                            {isVisitorMsg && <Globe className="w-3 h-3 text-emerald-600" strokeWidth={2} />}
                            {isVisitorMsg
                              ? (chat.visitor?.visitor_name || chat.visitor?.visitor_email || 'Visitor')
                              : msg.sender?.full_name || (() => {
                                  if (msg.message_type === 'task_comment') {
                                    try { return JSON.parse(msg.content).author_name || 'Portale'; } catch { return 'Portale'; }
                                  }
                                  return 'Sistema';
                                })()}
                          </div>
                        )}
                        {msg.reply_to?.content && (
                          <div className={`flex items-start gap-1 mb-1 ${isOwn ? 'justify-end' : ''}`}>
                            <CornerDownRight className="w-3 h-3 mt-0.5" style={{ color: '#787774' }} strokeWidth={1.5} />
                            <div
                              className="text-[11px] rounded px-2 py-0.5 max-w-[180px] truncate"
                              style={{ backgroundColor: 'rgba(55, 53, 47, 0.08)', color: '#37352F' }}
                            >
                              {msg.reply_to.content}
                            </div>
                          </div>
                        )}
                        <div
                          className="rounded-2xl px-4 py-2"
                          style={(() => {
                            const isForward = msg.message_type === 'whatsapp_forward' || (msg.content?.startsWith('{') && msg.content?.includes('"request_type"'));
                            const isTaskComment = msg.message_type === 'task_comment';
                            const isCrmShare = msg.message_type === 'crm_share';
                            const opacity = msg._status === 'sending' ? 0.7 : 1;
                            if (isForward || isTaskComment || isCrmShare) return { backgroundColor: 'transparent', color: '#37352F', padding: 0, opacity };
                            if (msg._status === 'failed') return { backgroundColor: '#FEE2E2', color: '#111B21', border: '1px solid rgba(239,68,68,0.3)', opacity: 1 };
                            if (isOwn) return { backgroundColor: '#DCF8C6', color: '#111B21', opacity };
                            if (isVisitorMsg) return { backgroundColor: '#F1F5F9', color: '#0F172A', border: '1px solid rgba(15,23,42,0.08)', opacity };
                            return { backgroundColor: '#FFFFFF', color: '#111B21', border: '1px solid rgba(0,0,0,0.08)', opacity };
                          })()}
                        >
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => handleEditKeyDown(e, msg.id)}
                                className="flex-1 bg-white/20 rounded px-2 py-1 text-sm outline-none"
                                autoFocus
                              />
                              <button onClick={() => handleEdit(msg.id)} className="hover:opacity-80"><Check className="w-4 h-4" strokeWidth={2} /></button>
                              <button onClick={cancelEditing} className="hover:opacity-80"><XCircle className="w-4 h-4" strokeWidth={1.5} /></button>
                            </div>
                          ) : (
                            <MessageContent message={msg} isOwn={isOwn} isEdited={msg.is_edited} onImageClick={openLightbox} />
                          )}
                        </div>
                        {/* Reaction bar */}
                        {(msg.reactions || []).length > 0 && (
                          <div className={`flex flex-wrap items-center gap-1 mt-1 ${isOwn ? 'justify-end' : ''}`}>
                            {(msg.reactions || []).map(reaction => {
                              const hasReacted = reaction.user_ids.includes(userProfile?.id || '');
                              return (
                                <button
                                  key={reaction.emoji}
                                  onClick={() => hasReacted
                                    ? removeReaction(msg.id, msg.chat_id, reaction.emoji)
                                    : addReaction(msg.id, msg.chat_id, reaction.emoji)
                                  }
                                  className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs transition-colors"
                                  style={{
                                    backgroundColor: hasReacted ? 'rgba(35,131,226,0.12)' : 'rgba(55,53,47,0.06)',
                                    border: `1px solid ${hasReacted ? 'rgba(35,131,226,0.3)' : 'rgba(55,53,47,0.1)'}`,
                                    color: hasReacted ? '#2383E2' : '#37352F',
                                  }}
                                >
                                  <span>{reaction.emoji}</span>
                                  <span className="ml-0.5 font-medium">{reaction.count}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {/* Status indicatori: sending / failed */}
                        {msg._status === 'sending' && (
                          <div className={`flex items-center gap-1 mt-1 text-[10px] ${isOwn ? 'justify-end mr-1' : 'ml-1'}`} style={{ color: '#667781' }}>
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            <span>Invio in corso...</span>
                          </div>
                        )}
                        {msg._status === 'failed' && (
                          <div className={`flex items-center gap-1.5 mt-1 text-[10px] ${isOwn ? 'justify-end mr-1' : 'ml-1'}`}>
                            <AlertCircle className="w-3 h-3 text-red-500" strokeWidth={2} />
                            <span style={{ color: '#EF4444' }}>Invio fallito</span>
                            <button
                              onClick={() => retryMessage(msg.id, msg.chat_id).catch(() => {})}
                              className="flex items-center gap-0.5 text-[10px] font-medium hover:opacity-80"
                              style={{ color: '#2383E2' }}
                            >
                              <RefreshCw className="w-3 h-3" strokeWidth={2} />
                              Riprova
                            </button>
                            <button
                              onClick={() => discardFailedMessage(msg.id, msg.chat_id)}
                              className="flex items-center gap-0.5 text-[10px] font-medium hover:opacity-80"
                              style={{ color: '#9B9A97' }}
                            >
                              <XCircle className="w-3 h-3" strokeWidth={1.5} />
                              Elimina
                            </button>
                          </div>
                        )}
                        {showTimestamp && !msg._status && (
                          <div
                            className={`flex items-center gap-1 mt-1 text-[10px] ${isOwn ? 'justify-end mr-1' : 'ml-1'}`}
                            style={{ color: '#667781' }}
                          >
                            <span>{formatDistanceToNow(new Date(msg.created_at), { addSuffix: false, locale: it })}</span>
                            {isOwn && (
                              <span title={msg.read_by?.length ? `Letto da ${msg.read_by.length}` : 'Inviato'}>
                                {msg.read_by && msg.read_by.filter(id => id !== userProfile?.id).length > 0
                                  ? <CheckCheck className="w-3.5 h-3.5" style={{ color: '#53BDEB' }} strokeWidth={2} />
                                  : <Check className="w-3 h-3" style={{ color: '#667781' }} strokeWidth={2} />}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Thread badge */}
                        {(msg.thread_message_count ?? 0) > 0 && (
                          <button
                            onClick={() => setThreadRootMessage(msg)}
                            className={`flex items-center gap-1 mt-1 text-[11px] font-medium ${isOwn ? 'ml-auto mr-1' : 'ml-1'}`}
                            style={{ color: '#2383E2' }}
                          >
                            <Reply className="w-3 h-3" strokeWidth={2} />
                            <span>{msg.thread_message_count} {msg.thread_message_count === 1 ? 'risposta' : 'risposte'}</span>
                            {msg.thread_last_reply_at && (
                              <span className="text-[10px]" style={{ color: '#9B9A97' }}>
                                · {formatDistanceToNow(new Date(msg.thread_last_reply_at), { addSuffix: false, locale: it })}
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    {showSummaryAfterThis && summaryData && (
                      <div
                        className="mx-auto my-3 max-w-[90%] rounded-lg px-3.5 py-2.5"
                        style={{
                          background: 'linear-gradient(135deg, rgba(35,131,226,0.06) 0%, rgba(35,131,226,0.02) 100%)',
                          border: '1px solid rgba(35,131,226,0.12)',
                        }}
                      >
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Sparkles className="w-3 h-3" style={{ color: '#2383E2' }} strokeWidth={2} />
                          <span className="text-[11px] font-semibold" style={{ color: '#2383E2' }}>Riassunto</span>
                          <span className="flex items-center gap-1.5 ml-auto">
                            {summaryData.generatedAt && (
                              <span className="text-[10px]" style={{ color: '#9ca3af' }}>
                                {new Date(summaryData.generatedAt).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                            <button
                              onClick={handleRegenerateSummary}
                              disabled={summaryLoading}
                              className="text-[10px] hover:underline disabled:opacity-50"
                              style={{ color: '#2383E2' }}
                              title="Rigenera riassunto"
                            >
                              {summaryLoading ? '...' : '↻'}
                            </button>
                          </span>
                        </div>
                        <p className="text-[11px] leading-relaxed" style={{ color: '#37352F' }}>
                          {summaryData.text}
                        </p>
                        {summaryData.nextSteps && summaryData.nextSteps.length > 0 && (
                          <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(35,131,226,0.1)' }}>
                            <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#2383E2' }}>
                              Next steps
                            </span>
                            <ul className="mt-1 space-y-0.5">
                              {summaryData.nextSteps.map((step, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-[11px]" style={{ color: '#37352F' }}>
                                  <span className="mt-0.5 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: '#2383E2' }} />
                                  {step}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    </React.Fragment>
                  );
                })
                }
                </>
              )}
              {/* Upload in corso */}
              {uploadingItems.map(item => (
                <div key={item.id} className="flex justify-end">
                  <div className="max-w-[75%]">
                    <div
                      className="rounded-2xl px-4 py-3 min-w-[180px]"
                      style={{ backgroundColor: '#2383E2', color: '#fff' }}
                    >
                      {item.preview ? (
                        <div className="relative mb-2">
                          <img
                            src={item.preview}
                            alt={item.name}
                            className="rounded-xl max-w-full opacity-70"
                            style={{ maxHeight: 160, objectFit: 'cover' }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-white" strokeWidth={2} />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 mb-2">
                          <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" strokeWidth={2} />
                          <span className="text-sm truncate">{item.name}</span>
                        </div>
                      )}
                      {/* Progress bar */}
                      <div className="w-full rounded-full overflow-hidden" style={{ height: 3, backgroundColor: 'rgba(255,255,255,0.25)' }}>
                        <div
                          className="h-full rounded-full transition-all duration-200"
                          style={{ width: `${item.progress}%`, backgroundColor: '#fff' }}
                        />
                      </div>
                      <div className="text-[10px] mt-1 text-right" style={{ opacity: 0.75 }}>
                        {item.progress < 100 ? `${item.progress}%` : 'Invio...'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {/* Loading summary (quando non c'è ancora lastMessageId o non matcha nessun messaggio) */}
              {summaryLoading && !summaryData && (
                <div
                  className="mx-auto my-3 max-w-[90%] rounded-lg px-3.5 py-2.5"
                  style={{
                    background: 'linear-gradient(135deg, rgba(35,131,226,0.06) 0%, rgba(35,131,226,0.02) 100%)',
                    border: '1px solid rgba(35,131,226,0.12)',
                  }}
                >
                  <div className="flex items-center gap-2 text-[11px]" style={{ color: '#787774' }}>
                    <Sparkles className="w-3 h-3" style={{ color: '#2383E2' }} strokeWidth={2} />
                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                    Generazione riassunto...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply preview - Notion style */}
            {replyingTo && (
              <div
                className="px-4 py-2 flex items-center justify-between"
                style={{ backgroundColor: '#F7F7F5', borderTop: '1px solid rgba(55, 53, 47, 0.09)' }}
              >
                <div className="flex items-center gap-2 text-sm min-w-0" style={{ color: '#37352F' }}>
                  <Reply className="w-4 h-4 flex-shrink-0" style={{ color: '#2383E2' }} strokeWidth={1.5} />
                  <span className="truncate">{replyingTo.sender?.full_name}: {replyingTo.content}</span>
                </div>
                <button onClick={cancelReply} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)]">
                  <X className="w-4 h-4" style={{ color: '#787774' }} strokeWidth={1.5} />
                </button>
              </div>
            )}

            {/* Staged files preview */}
            {stagedFiles.length > 0 && (
              <div
                className="px-4 py-2 flex items-center gap-2 overflow-x-auto"
                style={{ backgroundColor: '#F7F7F5', borderTop: '1px solid rgba(55,53,47,0.09)' }}
              >
                {stagedFiles.map(sf => (
                  <div key={sf.id} className="relative flex-shrink-0 group">
                    {sf.preview ? (
                      <img
                        src={sf.preview}
                        alt={sf.file.name}
                        className="rounded-lg object-cover"
                        style={{ width: 56, height: 56 }}
                      />
                    ) : (
                      <div
                        className="rounded-lg flex flex-col items-center justify-center"
                        style={{ width: 56, height: 56, backgroundColor: 'rgba(55,53,47,0.08)' }}
                      >
                        {sf.attachmentType === 'video' ? <Film className="w-5 h-5" style={{ color: '#787774' }} strokeWidth={1.5} />
                          : sf.attachmentType === 'audio' ? <Music2 className="w-5 h-5" style={{ color: '#787774' }} strokeWidth={1.5} />
                          : <FileText className="w-5 h-5" style={{ color: '#787774' }} strokeWidth={1.5} />}
                        <span className="text-[8px] mt-0.5 truncate max-w-[48px]" style={{ color: '#787774' }}>
                          {sf.file.name.split('.').pop()?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => removeStagedFile(sf.id)}
                      className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2.5 h-2.5" strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
                <span className="text-[11px] ml-1 flex-shrink-0" style={{ color: '#787774' }}>
                  {stagedFiles.length} file — scrivi un messaggio e invia
                </span>
              </div>
            )}

            {/* Typing indicator */}
            {currentTypingUsers.length > 0 && (
              <div
                className="px-4 py-1.5 text-xs flex items-center gap-1"
                style={{ backgroundColor: '#F7F7F5', borderTop: '1px solid rgba(55,53,47,0.06)', color: '#787774' }}
              >
                <span>
                  {currentTypingUsers.length === 1
                    ? `${currentTypingUsers[0].userName} sta scrivendo`
                    : `${currentTypingUsers.map(t => t.userName).join(', ')} stanno scrivendo`}
                </span>
                <span className="flex gap-0.5 items-end mb-px">
                  <span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            )}

            {/* Input - Notion style */}
            <div
              className="p-4 bg-white space-y-2"
              style={{ borderTop: '1px solid rgba(55, 53, 47, 0.09)' }}
            >
              {/* Hidden file input generico */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={e => { handleFileSelect(e.target.files); e.target.value = ''; }}
              />


              {/* AudioRecorder */}
              {isRecording ? (
                <AudioRecorder
                  onSend={handleAudioSend}
                  onCancel={() => setIsRecording(false)}
                  uploading={uploading}
                />
              ) : (
                <div className="flex items-center gap-2">
                  {/* Toolbar allegati */}
                  <div className="flex items-center gap-0.5">
                    <button
                      ref={emojiPickerAnchorRef}
                      onClick={() => setShowEmojiPicker(o => !o)}
                      className="p-1.5 rounded transition-colors"
                      style={{ color: showEmojiPicker ? '#2383E2' : '#787774' }}
                      title="Emoji"
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <Smile className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="p-1.5 rounded transition-colors disabled:opacity-40"
                      style={{ color: '#787774' }}
                      title="Allega file (immagini, video, audio, documenti — max 10 MB)"
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <Paperclip className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => setIsRecording(true)}
                      disabled={uploading}
                      className="p-1.5 rounded transition-colors disabled:opacity-40"
                      style={{ color: '#787774' }}
                      title="Registra audio"
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <Mic className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setShowGifPicker(o => !o)}
                        className="p-1.5 rounded transition-colors"
                        style={{ color: showGifPicker ? '#2383E2' : '#787774' }}
                        title="GIF"
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <span className="text-[11px] font-bold leading-none">GIF</span>
                      </button>
                      {showGifPicker && (
                        <GifPicker
                          onSelect={async (gifUrl) => {
                            setShowGifPicker(false);
                            if (!selectedChatId) return;
                            const { sendMessage } = useTeamChatStore.getState();
                            await sendMessage(selectedChatId, gifUrl);
                          }}
                          onClose={() => setShowGifPicker(false)}
                        />
                      )}
                    </div>
                    <div className="relative">
                      <button
                        onClick={handleMeetButtonClick}
                        disabled={creatingMeet || uploading}
                        className="p-1.5 rounded transition-colors disabled:opacity-40"
                        style={{ color: creatingMeet ? '#2383E2' : '#787774' }}
                        title={
                          meetGoogleAvailable && teamsMeetAvailable
                            ? 'Crea meeting (Google Meet / Microsoft Teams)'
                            : teamsMeetAvailable && !meetGoogleAvailable
                              ? 'Crea meeting Microsoft Teams'
                              : 'Crea Google Meet'
                        }
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        {creatingMeet ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Video className="w-4 h-4" strokeWidth={1.5} />
                        )}
                      </button>
                      {showMeetMenu && meetGoogleAvailable && teamsMeetAvailable && (
                        <>
                          <div className="fixed inset-0 z-[9998]" onClick={() => setShowMeetMenu(false)} />
                          <div
                            className="absolute bottom-full mb-1 right-0 z-[9999] bg-white rounded-xl shadow-lg py-1 min-w-[180px]"
                            style={{ border: '1px solid rgba(55,53,47,0.1)' }}
                          >
                            <button
                              onClick={() => { setShowMeetMenu(false); handleCreateMeet('google_meet'); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                              style={{ color: '#37352F' }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <Video className="w-4 h-4" strokeWidth={1.5} />
                              Google Meet
                            </button>
                            <button
                              onClick={() => { setShowMeetMenu(false); handleCreateMeet('microsoft_teams'); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                              style={{ color: '#37352F' }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                              <Video className="w-4 h-4" strokeWidth={1.5} />
                              Microsoft Teams
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* @Mention dropdown */}
                  <div className="relative flex-1">
                    {mentionQuery !== null && mentionResults.length > 0 && (
                      <div
                        className="absolute bottom-full mb-1 left-0 z-50 bg-white rounded-xl shadow-lg py-1 min-w-[200px]"
                        style={{ border: '1px solid rgba(55,53,47,0.1)' }}
                      >
                        {mentionResults.map((user, idx) => (
                          <button
                            key={user.id}
                            onClick={() => handleMentionSelect(user)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors"
                            style={{ backgroundColor: idx === mentionIndex ? 'rgba(55,53,47,0.06)' : 'transparent', color: '#37352F' }}
                            onMouseEnter={() => setMentionIndex(idx)}
                          >
                            {user.profile_image_url ? (
                              <img src={user.profile_image_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold flex-shrink-0" style={{ backgroundColor: 'rgba(55,53,47,0.12)' }}>
                                {user.full_name[0]?.toUpperCase()}
                              </div>
                            )}
                            <span className="font-medium">{user.full_name}</span>
                          </button>
                        ))}
                      </div>
                    )}

                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={e => {
                      handleInputChange(e.target.value);
                      detectMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
                    }}
                    onKeyDown={(e) => {
                      // Gestisci navigazione mention dropdown
                      if (mentionQuery !== null && mentionResults.length > 0) {
                        if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1)); return; }
                        if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMentionSelect(mentionResults[mentionIndex]); return; }
                        if (e.key === 'Escape') { setMentionQuery(null); return; }
                      }
                      // Se ci sono file staged, Enter invia con allegati
                      if (stagedFiles.length > 0 && e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        handleSendWithAttachments();
                        return;
                      }
                      handleKeyDown(e);
                    }}
                    placeholder="Scrivi un messaggio..."
                    className="w-full px-4 py-2 rounded-full outline-none transition-all"
                    style={{
                      color: '#37352F',
                      border: '1px solid rgba(55, 53, 47, 0.16)'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#2383E2';
                      e.target.style.boxShadow = '0 0 0 2px rgba(35, 131, 226, 0.2)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(55, 53, 47, 0.16)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  </div>{/* chiude div relative flex-1 */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleSendWithAttachments}
                      disabled={(!inputValue.trim() && stagedFiles.length === 0) || uploading}
                      className="p-2 rounded-full text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#2383E2' }}
                      onMouseEnter={(e) => !uploading && (e.currentTarget.style.backgroundColor = '#1a6cb8')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2383E2')}
                    >
                      {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={1.5} />}
                    </button>
                    {/* Schedule send */}
                    <div className="relative">
                      <button
                        onClick={() => setShowSchedulePopover(o => !o)}
                        disabled={!inputValue.trim()}
                        className="p-1.5 rounded-full transition-colors disabled:opacity-30"
                        style={{ color: showSchedulePopover ? '#2383E2' : '#787774' }}
                        title="Programma invio"
                      >
                        <Clock className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      {showSchedulePopover && (
                        <ScheduleMessagePopover
                          onSchedule={async (date) => {
                            setShowSchedulePopover(false);
                            if (!selectedChatId || !inputValue.trim()) return;
                            try {
                              const { scheduleMessage } = useTeamChatStore.getState();
                              await scheduleMessage(selectedChatId, inputValue.trim(), date, replyingTo?.id);
                              handleInputChange('');
                              if (replyingTo) cancelReply();
                              toast.success(`Messaggio programmato per ${date.toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`);
                            } catch (err) {
                              toast.error('Errore nella programmazione');
                            }
                          }}
                          onClose={() => setShowSchedulePopover(false)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* Pinned messages panel */}
      {showPinnedPanel && selectedChatId && (
        <PinnedMessagesPanel
          chatId={selectedChatId}
          onClose={() => setShowPinnedPanel(false)}
          onScrollToMessage={(id) => { setShowPinnedPanel(false); handleNavigateToMessage(id); }}
        />
      )}
      {/* Thread panel */}
      {threadRootMessage && selectedChatId && (
        <ThreadPanel
          chatId={selectedChatId}
          rootMessage={threadRootMessage}
          onClose={() => setThreadRootMessage(null)}
          organizationUsers={organizationUsers}
        />
      )}
      </div>{/* chiude flex-row wrapper */}
    </div>
  );
}
